/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import { Modal } from '@common/Modal';
import { Stack, alpha } from '@mui/material';
import { Button, LoadingButton } from '@common/Button';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteLoader, LoaderConfiguration } from '@requests/loaders';
import { useSnackbar } from 'notistack';
import { Close } from '@mui/icons-material';
import { useTheme } from '../../../styles';

interface Props {
  open: string | null;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  loader: LoaderConfiguration | undefined;
}

export const RemoveLoader: React.FC<Props> = ({ open, setOpen, setPage, loader }) => {
  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  const queryClient = useQueryClient();

  const { mutateAsync: remove, status: removeStatus } = useMutation({
    mutationFn: deleteLoader,
    onSuccess: (data: any): void => {
      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });

      if (data?.error) {
        enqueueSnackbar('Failed to remove loader!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      setPage(0);
      setOpen(null);
    },
  });

  const removeLoader = () => {
    if (!loader) return;

    remove({ bucket_name: loader.bucket_name, service_id: service_id as string });
  };

  return (
    <Modal
      open={Boolean(open)}
      setOpen={setOpen}
      background={alpha(theme.text.standard, 0.06)}
      header='Remove Loader Configuration'
      info='WARNING! You are about to remove the loader from this bucket. This will stop the scheduler from refreshing the files in this bucket. It does not remove the files or embeddings. After removing this loader, you will not be able to reconfigure it for this bucket unless you remove all files first. Proceed with caution.'
      icon={<Close sx={{ color: theme.telnyx.red }} />}
      actions={
        <Stack direction='row' spacing={1}>
          <Button onClick={() => setOpen(null)}>Cancel</Button>
          <LoadingButton
            minHeight='32px'
            minWidth='80px'
            loading={removeStatus === 'pending'}
            customColor='red'
            onClick={removeLoader}
          >
            Remove Loader
          </LoadingButton>
        </Stack>
      }
    />
  );
};
